import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { workspaceApi } from '@/lib/api'
import { Users, Settings, Lock, Globe, GitBranch, FolderOpen } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import type { WorkspaceVisibility } from '@/types/workspace'

const VISIBILITY_LABEL: Record<WorkspaceVisibility, string> = {
  private: 'Privat',
  public: 'Publik',
  invite_only: 'Invite Only',
}

export default function WorkspaceDetailPage() {
  const { slug } = useParams<{ slug: string }>()

  const { data, isLoading, error } = useQuery({
    queryKey: ['workspace', slug],
    queryFn: () => workspaceApi.getWorkspace(slug!),
    enabled: !!slug,
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-4 w-64 animate-pulse rounded bg-muted" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <p className="text-sm text-destructive">
        Workspace tidak ditemukan atau kamu tidak memiliki akses.
      </p>
    )
  }

  const VisibilityIcon = data.visibility === 'public' ? Globe : Lock

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{data.name}</h1>
          <p className="text-sm text-muted-foreground">/{data.slug}</p>
          {data.description && (
            <p className="mt-1 text-sm text-muted-foreground">{data.description}</p>
          )}
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
          <VisibilityIcon className="h-3 w-3" />
          {VISIBILITY_LABEL[data.visibility]}
        </span>
      </div>

      {/* Quick nav */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Link
          to={`/workspaces/${slug}/projects`}
          className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:bg-secondary"
        >
          <FolderOpen className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm font-medium">Project</span>
        </Link>
        <Link
          to={`/workspaces/${slug}/members`}
          className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:bg-secondary"
        >
          <Users className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm font-medium">Member</span>
        </Link>
        <Link
          to={`/workspaces/${slug}/pipeline`}
          className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:bg-secondary"
        >
          <GitBranch className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm font-medium">Pipeline</span>
        </Link>
        <Link
          to={`/workspaces/${slug}/settings`}
          className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:bg-secondary"
        >
          <Settings className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm font-medium">Pengaturan</span>
        </Link>
      </div>

      {/* Meta */}
      <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground space-y-1">
        <p>Dibuat: {formatDate(data.created_at)}</p>
        <p>Diperbarui: {formatDate(data.updated_at)}</p>
      </div>
    </div>
  )
}
